import React, { useState } from 'react';

import { MenuProps } from 'antd';
import styled from 'styled-components';

import { hasPermissionOrAdmin } from 'helpers/permissions';
import { VIEW_PERMISSIONS } from 'permissions/views';
import { useAppSelector } from 'store/hook';

import Button from '../../lib/Button';
import Dropdown from '../../lib/Dropdown';
import { useCommunityViewContext } from '../CommunityViewContext';
import DeleteViewModal from '../modals/DeleteViewModal';
import DuplicateViewModal from '../modals/DuplicateViewModal';
import RenameViewModal from '../modals/RenameViewModal';
import ResetLayoutModal from '../modals/ResetLayoutModal';

const Container = styled.div`
  .icon-wrapper {
    padding: 7px;
  }
`;

const HeaderViewActionsControls = () => {
  const { view, isViewCMA, isViewSalesBattlecard, triggerExpandAllRef, triggerCollapseAllRef } =
    useCommunityViewContext();

  const [showDuplicateModal, setShowDuplicateModal] = useState<boolean>(false);
  const [showRenameModal, setShowRenameModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showResetLayoutModal, setResetLayoutModal] = useState<boolean>(false);

  const currentUser = useAppSelector((state) => state.auth.currentUser);
  const getCMAItems = (): MenuProps['items'] => [
    {
      label: 'Expand All',
      key: 'expand-all',
      onClick: () => triggerExpandAllRef.current(),
    },
    {
      label: 'Collapse All',
      key: 'collapse-all',
      onClick: () => triggerCollapseAllRef.current(),
    },
  ];

  const getSalesBattlecardItems = (): MenuProps['items'] => [
    {
      label: 'Reset Layout',
      key: 'reset-layout',
      onClick: () => setResetLayoutModal(true),
    },
  ];

  const getStandardViewItems = (): MenuProps['items'] => {
    let categoryPermissions;
    if (view?.default) {
      categoryPermissions = VIEW_PERMISSIONS.OVERVIEW;
    } else {
      categoryPermissions = VIEW_PERMISSIONS.CUSTOM;
    }

    const canCreateCustomView = hasPermissionOrAdmin(currentUser, [VIEW_PERMISSIONS.CUSTOM.ADD]);
    const canChangeView = hasPermissionOrAdmin(currentUser, [categoryPermissions.CHANGE]);
    const canDeleteView = hasPermissionOrAdmin(currentUser, [categoryPermissions.DELETE]);

    const items: MenuProps['items'] = [];

    if (canCreateCustomView) {
      items.push({
        label: 'Duplicate View',
        key: 'duplicate',
        onClick: () => setShowDuplicateModal(true),
      });
    }

    if (canChangeView) {
      items.push({
        label: 'Rename View',
        key: 'rename',
        disabled: view?.default,
        onClick: () => setShowRenameModal(true),
      });
    }

    if (canDeleteView) {
      items.push({
        label: 'Delete View',
        key: 'delete',
        disabled: view?.default,
        onClick: () => setShowDeleteModal(true),
      });
    }

    items.push({
      label: 'Reset Layout',
      key: 'reset-layout',
      onClick: () => setResetLayoutModal(true),
    });

    return items;
  };

  let items: MenuProps['items'] = [];
  if (isViewCMA) {
    items = getCMAItems();
  } else if (isViewSalesBattlecard) {
    items = getSalesBattlecardItems();
  } else {
    items = getStandardViewItems();
  }

  return (
    <Container>
      <DuplicateViewModal open={showDuplicateModal} onClose={() => setShowDuplicateModal(false)} />
      <RenameViewModal open={showRenameModal} onClose={() => setShowRenameModal(false)} />
      <DeleteViewModal open={showDeleteModal} onClose={() => setShowDeleteModal(false)} />
      <ResetLayoutModal open={showResetLayoutModal} onClose={() => setResetLayoutModal(false)} />

      <Dropdown menu={{ items }}>
        <Button className="icon-wrapper" data-test-id="more-options-btn">
          <img src="/icons/elipsis.svg" alt="custom" />
        </Button>
      </Dropdown>
    </Container>
  );
};

export default HeaderViewActionsControls;
